"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Footer } from "@/components/footer";
import { PromptCard } from "@/components/prompt-card";
import { Button } from "@/components/ui/button";
import { categories } from "@/lib/mock-data";
import type { Prompt } from "@/lib/types";
import { fetchPromptsPublic } from "@/lib/supabase/prompts";

export function HomeClient() {
  const t = useTranslations("home");
  const [selectedCategory, setSelectedCategory] = useState("전체");
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPrompts() {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const data = await fetchPromptsPublic();
        if (cancelled) return;
        setPrompts(data);
      } catch (e) {
        if (cancelled) return;
        setPrompts([]);
        setErrorMessage(e instanceof Error ? e.message : "Failed to load prompts");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadPrompts();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredPrompts = useMemo(() => {
    if (selectedCategory === "전체") return prompts;
    return prompts.filter((p) => p.category === selectedCategory);
  }, [prompts, selectedCategory]);

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-balance">{t("heroTitle")}</h1>
              <p className="text-lg text-muted-foreground text-pretty leading-relaxed">
                {t("heroDescription")}
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">1,500+</div>
                <div className="text-muted-foreground">{t("stats.prompts")}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">300+</div>
                <div className="text-muted-foreground">{t("stats.creators")}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">10,000+</div>
                <div className="text-muted-foreground">{t("stats.customers")}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 border-b bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category)}
                  className="whitespace-nowrap"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Prompts Grid */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            {errorMessage ? (
              <div className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                {errorMessage}
              </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading ? (
                <div className="text-muted-foreground">Loading...</div>
              ) : (
                filteredPrompts.map((prompt) => <PromptCard key={prompt.id} prompt={prompt} />)
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

